import { useMutation, useQuery } from "@apollo/client/react";
import { InboxIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../components/core";
import { Button } from "../../../components/form";
import { LimitBy, TableCursor } from "../../../components/table";
import { NotifyType, useNotifyContext } from "../../../contexts/NotifyContext";
import { GET_ROLES, REMOVE_ROLE } from "../../../graphql/user-management/roles";
import { AlertModal, AlertType, useAlert } from "../../../hooks/useAlert";
import { formatDate } from "../../../utils";
import { isAdmin } from "../../auth";
import { AuthShield } from "../../auth/core";

type Permissions = {
  id: string;
  permissionName: string;
  permissionDisplayName: string;
  permissionDescription: string;
  createdAt: string;
  status: string;
};

type Role = {
  id: string;
  roleName: string;
  roleDescription: string;
  permissions: Permissions;
  createdAt: string;
  status: boolean;
};

const RoleList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);
  const { addNotify } = useNotifyContext();

  const { data, loading, error, refetch } = useQuery<{
    fetchRoles: Role[];
  }>(GET_ROLES);
  const roles = useMemo(
    () =>
      data?.fetchRoles.flatMap((item) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })) || [],
    [data?.fetchRoles]
  );

  const [deleteRole] = useMutation(REMOVE_ROLE);
  const handleDelete = async (id: string) => {
    return await deleteRole({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.roleDelete) {
          const deletedRole = data?.roleDelete?.role;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Role ${deletedRole.roleName} is deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Role delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Role delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<Role, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "roleName",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.roleName,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) =>
          !isAdmin(props.row.original) ? (
            <div className="flex space-x-2 md:space-x-4">
              <AuthShield access={["update-roles"]}>
                <Button
                  type="link"
                  href={`/user-management/roles/${props.row.original.id}`}
                  variant="icon"
                  className="text-blue-500 hover:text-blue-600"
                >
                  <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                  <span className="sr-only">
                    Edit, {props.row.original.roleName}
                  </span>
                </Button>
              </AuthShield>
              <AuthShield access={["destroy-roles"]}>
                <Button
                  variant="icon"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => {
                    setAlert({
                      id: props.row.original.id,
                      name: props.row.original.roleName,
                    });
                  }}
                >
                  <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                  <span className="sr-only">
                    Delete, {props.row.original.roleName}
                  </span>
                </Button>
              </AuthShield>
            </div>
          ) : null,
      },
    ],
    []
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title="Roles"
        heading="Roles"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Roles",
            href: "/user-management/roles",
          },
          {
            name: RoleListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {RoleListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {RoleListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-roles"]}>
              <Button type="link" href="/user-management/roles/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add role
              </Button>
            </AuthShield>
          </div>
        </div>

        <TableCursor
          data={roles}
          columns={columns}
          loading={loading}
          totalRows={roles.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No roles found"
          fallbackText="You can add a new role by clicking on add role button."
        />
      </div>
    </>
  );
};

export default RoleList;
export const RoleListResource: ResourceProps = {
  name: "Roles",
  description: "A list of roles",
  access: ["read-roles"],
  path: "roles",
};
